/**
 * Copyright © Veeam Software Group GmbH.
 */

import React from 'react';
import {
    Form,
    FormLayout,
    FieldLayout,
    StackView,
    STACK_DIRECTION,
    STACK_GAP,
    Checkbox,
    CONTROL_SIZE,
    Text,
    LinkButton,
    Combobox,
    Radiobutton,
    usePageWizardStore,
} from '@veeam-vspc/components';
import { Vb365CopyJobSchedulePolicy } from '@veeam-vspc/models/rest';

import type { Vb365CopyJob } from '@veeam-vspc/models/rest';
import type { WizardStep, WizardStepData } from '@veeam-vspc/components';

import { StepLayout } from 'components/layouts/StepLayout';
import { TimeField } from 'components/controls/TimeField';
import { useLang } from 'views/providers/LangProvider/hooks';
import { getPeriodicallyReadableValues } from '../../../../helpers';
import { ConfigureBackupWindow } from './components';

import type { LangsServiceBase } from 'core/services/langs/interfaces';

export const getScheduleStep = (lang: LangsServiceBase, isScheduleHidden: boolean): WizardStep<Vb365CopyJob> => ({
    title: lang.SCHEDULE,
    isHidden: () => isScheduleHidden,
    render: data => <ScheduleStep {...data} />,
});

const { SchedulePolicyTypeEnum, DailyTypeEnum, PeriodicallyEveryEnum } = Vb365CopyJobSchedulePolicy;

const ScheduleStep = ({ data, validationState, onDataChange }: WizardStepData<Vb365CopyJob>) => {
    const lang = useLang();
    const wizardContext = usePageWizardStore();

    const handleDataChange = (data: Vb365CopyJob) => {
        onDataChange(data);
    };

    return (
        <StepLayout
            title={lang.SCHEDULE}
            description={lang.CHOOSE_WHEN_YOU_WANT_BACKUP_JOB}
        >
            <Form
                value={data}
                validationState={validationState}
                onChange={handleDataChange}
            >
                <FormLayout>
                    <FieldLayout>
                        <Radiobutton
                            name='schedulePolicy.schedulePolicyType'
                            value={SchedulePolicyTypeEnum.Immediate}
                        >
                            {lang.IMMEDIATELY}
                        </Radiobutton>
                    </FieldLayout>

                    <Text>{lang.COPIES_EVERY_RESTORE_POINT_CREATED_BY_SELECTED_JOB}</Text>

                    <FieldLayout suffix={(
                        <StackView gap={STACK_GAP.m} direction={STACK_DIRECTION.row}>
                            <FieldLayout
                                disabled={data.schedulePolicy.schedulePolicyType !== SchedulePolicyTypeEnum.DailyAtTime}
                            >
                                <TimeField
                                    name='schedulePolicy.dailyTime'
                                    shortFormat
                                    minutesInterval={1}
                                    utc
                                />
                            </FieldLayout>

                            <FieldLayout
                                disabled={data.schedulePolicy.schedulePolicyType !== SchedulePolicyTypeEnum.DailyAtTime}
                            >
                                <Combobox
                                    name='schedulePolicy.dailyType'
                                    data={[
                                        { value: DailyTypeEnum.Everyday, text: lang.EVERYDAY },
                                        { value: DailyTypeEnum.Workdays, text: lang.WORKDAYS },
                                        { value: DailyTypeEnum.Weekends, text: lang.WEEKENDS },
                                        { value: DailyTypeEnum.Monday, text: lang.MONDAY },
                                        { value: DailyTypeEnum.Tuesday, text: lang.TUESDAY },
                                        { value: DailyTypeEnum.Wednesday, text: lang.WEDNESDAY },
                                        { value: DailyTypeEnum.Thursday, text: lang.THURSDAY },
                                        { value: DailyTypeEnum.Friday, text: lang.FRIDAY },
                                        { value: DailyTypeEnum.Saturday, text: lang.SATURDAY },
                                        { value: DailyTypeEnum.Sunday, text: lang.SUNDAY },
                                    ]}
                                    valueGetter={x => x.value}
                                    textGetter={x => x.text}
                                    autoSelectFirstItem
                                />
                            </FieldLayout>
                        </StackView>
                    )}
                    >
                        <Radiobutton
                            name='schedulePolicy.schedulePolicyType'
                            value={SchedulePolicyTypeEnum.DailyAtTime}
                        >
                            {lang.DAILY_AT_THIS_TIME}
                        </Radiobutton>
                    </FieldLayout>

                    <FieldLayout
                        suffix={(
                            <StackView>
                                <FieldLayout
                                    disabled={data.schedulePolicy.schedulePolicyType !== SchedulePolicyTypeEnum.Periodically}
                                >
                                    <Combobox
                                        name='schedulePolicy.periodicallyEvery'
                                        data={[
                                            {
                                                value: PeriodicallyEveryEnum.Minutes5,
                                                text: getPeriodicallyReadableValues(lang)[PeriodicallyEveryEnum.Minutes5],
                                            },
                                            {
                                                value: PeriodicallyEveryEnum.Minutes10,
                                                text: getPeriodicallyReadableValues(lang)[PeriodicallyEveryEnum.Minutes10],
                                            },
                                            {
                                                value: PeriodicallyEveryEnum.Minutes15,
                                                text: getPeriodicallyReadableValues(lang)[PeriodicallyEveryEnum.Minutes15],
                                            },
                                            {
                                                value: PeriodicallyEveryEnum.Minutes30,
                                                text: getPeriodicallyReadableValues(lang)[PeriodicallyEveryEnum.Minutes30],
                                            },
                                            {
                                                value: PeriodicallyEveryEnum.Hours1,
                                                text: getPeriodicallyReadableValues(lang)[PeriodicallyEveryEnum.Hours1],
                                            },
                                            {
                                                value: PeriodicallyEveryEnum.Hours2,
                                                text: getPeriodicallyReadableValues(lang)[PeriodicallyEveryEnum.Hours2],
                                            },
                                            {
                                                value: PeriodicallyEveryEnum.Hours4,
                                                text: getPeriodicallyReadableValues(lang)[PeriodicallyEveryEnum.Hours4],
                                            },
                                            {
                                                value: PeriodicallyEveryEnum.Hours8,
                                                text: getPeriodicallyReadableValues(lang)[PeriodicallyEveryEnum.Hours8],
                                            },
                                        ]}
                                        valueGetter={x => x.value}
                                        textGetter={x => x.text}
                                        size={CONTROL_SIZE.m}
                                        autoSelectFirstItem
                                    />
                                </FieldLayout>
                            </StackView>
                        )}
                    >
                        <Radiobutton
                            name='schedulePolicy.schedulePolicyType'
                            value={SchedulePolicyTypeEnum.Periodically}
                        >
                            {lang.PERIODICALLY_EVERY}
                        </Radiobutton>
                    </FieldLayout>

                    <FieldLayout>
                        <Checkbox name='schedulePolicy.backupWindowEnabled'>
                            {lang.TERMINATE_JOB_IF_IT_EXCEEDS_ALLOWED_BACKUP_WINDOW}
                        </Checkbox>
                    </FieldLayout>

                    <LinkButton
                        onClick={() => wizardContext.openPanel(hide => <ConfigureBackupWindow hidePanel={hide} data={data} />)}
                        disabled={!data.schedulePolicy.backupWindowEnabled}
                    >
                        {lang.CONFIGURE_BACKUP_WINDOW}
                    </LinkButton>
                </FormLayout>
            </Form>
        </StepLayout>
    );
};
